<template>
  <div class="reactor-contracts-page p-4">
    <h2>Enterprise Contracts</h2>

    <b-card header="New contract" class="mb-4">
      <b-form @submit.prevent="create">
        <b-row>
          <b-col md="6">
            <b-form-group label="Client email">
              <b-form-input v-model="form.client_email" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Client display name">
              <b-form-input v-model="form.client_display_name" required />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <b-form-group label="Total hashrate (TH/s)">
              <b-form-input
                v-model.number="form.total_hashrate_th"
                type="number"
                min="1"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Per-miner hashrate (TH/s)">
              <b-form-input
                v-model.number="form.per_miner_hashrate_th"
                type="number"
                min="1"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Duration (days)">
              <b-form-input
                v-model.number="durationDays"
                type="number"
                min="1"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <b-form-group label="Start date (UTC)">
              <b-form-input
                v-model="form.start_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Pool ID">
              <b-form-input v-model="form.pool_id" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Price (USD cents)">
              <b-form-input
                v-model.number="form.price_cents_total"
                type="number"
                min="0"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Notes">
          <b-form-textarea v-model="form.notes" rows="2" />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="creating">
          {{ creating ? "Creating…" : "Create contract" }}
        </b-button>
        <span v-if="createError" class="text-danger ml-3">{{
          createError
        }}</span>
        <span v-if="createdId" class="text-success ml-3">
          Created contract #{{ createdId }}
        </span>
      </b-form>
    </b-card>

    <b-card header="Existing contracts">
      <div v-if="loading" class="text-muted">Loading…</div>
      <div v-else-if="loadError" class="text-danger">{{ loadError }}</div>
      <b-table v-else :items="contracts" :fields="fields" small striped hover>
        <template #cell(actions)="row">
          <b-button
            v-if="
              row.item.status === 'SCHEDULED' || row.item.status === 'ACTIVE'
            "
            size="sm"
            variant="outline-danger"
            @click="cancel(row.item, false)"
          >
            Cancel (graceful)
          </b-button>
          <b-button
            v-if="row.item.status === 'ACTIVE'"
            size="sm"
            variant="danger"
            class="ml-2"
            @click="cancel(row.item, true)"
          >
            Cancel (instant)
          </b-button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import api from "@/api";

export default {
  data() {
    return {
      loading: true,
      loadError: null,
      creating: false,
      createError: null,
      createdId: null,
      durationDays: 7,
      contracts: [],
      fields: [
        { key: "id", label: "#" },
        { key: "client_email", label: "Client" },
        { key: "total_hashrate_th", label: "Total TH/s" },
        { key: "miner_count", label: "Miners" },
        { key: "status", label: "Status" },
        { key: "start_date", label: "Start" },
        { key: "end_date", label: "End" },
        { key: "actions", label: "" },
      ],
      form: {
        client_email: "",
        client_display_name: "",
        total_hashrate_th: 1000,
        per_miner_hashrate_th: 100,
        start_date: "",
        pool_id: "",
        price_cents_total: 0,
        notes: "",
      },
    };
  },
  async created() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = null;
      const res = await api.reactorAdminListContracts();
      if (!res.ok) {
        this.loadError = res.data.error
          ? res.data.error.message || "Failed to load"
          : "Failed to load";
      } else {
        this.contracts = (res.data.result && res.data.result.contracts) || [];
      }
      this.loading = false;
    },
    async create() {
      this.creating = true;
      this.createError = null;
      this.createdId = null;
      const payload = {
        ...this.form,
        duration_secs: this.durationDays * 24 * 60 * 60,
        start_date: new Date(this.form.start_date).toISOString(),
      };
      const res = await api.reactorAdminCreateContract(payload);
      this.creating = false;
      if (!res.ok) {
        this.createError = res.data.error
          ? res.data.error.message || JSON.stringify(res.data.error)
          : "Failed";
      } else {
        this.createdId = res.data.result.id;
        await this.load();
      }
    },
    async cancel(contract, instant) {
      if (
        !confirm(
          `Cancel contract #${contract.id}${instant ? " immediately" : ""}?`
        )
      )
        return;
      const res = await api.reactorAdminCancelContract(contract.id, instant);
      if (!res.ok) {
        alert(
          `Cancel failed: ${
            res.data.error ? res.data.error.message : "unknown"
          }`
        );
        return;
      }
      await this.load();
    },
  },
};
</script>
